import { DebugLog } from '../debug-log';
import { isInternal } from '../environment-utils';
import { getMraidPolyfill, VIEWABLE_CHANGE_STRATEGY } from './mraid-polyfill';

export interface MraidProviderOptions {
  isMraidCompliant: boolean;
  appExchange?: string;
  dsp?: string;
}

type WindowWithMraid = Window & { mraid?: any };

export class MRAIDProvider {
  provideMraid(
    window: WindowWithMraid,
    options: MraidProviderOptions,
    done: () => void = () => {}
  ): void {
    this.initializeMraid(window, options, () => {
      const mraid = window.mraid;
      if (!mraid.__ogyPolyfill) {
        mraid.__ogyPolyfill = false;
      }
      mraid.openNoRedirect = this.createDefaultOpenNoRedirectHandler();
      window.mraid = mraid;
      done();
    });
  }

  initializeMraid(
    window: WindowWithMraid,
    options: MraidProviderOptions,
    done: () => void
  ): void {
    const isMraidLibLoaded = typeof window.mraid !== 'undefined';
    if (isMraidLibLoaded) {
      done();
      return;
    }
    if (!options.isMraidCompliant) {
      this.polyfill(window, options);
      done();
    } else {
      this.loadMraidWithFallback(window, options, done);
    }
  }

  loadMraidWithFallback(
    window: WindowWithMraid,
    options: MraidProviderOptions,
    done: () => void
  ): void {
    this.injectMRAIDScript(
      () => {
        done();
      },
      () => {
        this.polyfill(window, options);
        done();
      }
    );
  }

  createDefaultOpenNoRedirectHandler(): () => void {
    return () => {
      DebugLog.log('openNoRedirect is not ready yet');
    };
  }

  polyfill(window: WindowWithMraid, options: MraidProviderOptions): void {
    DebugLog.log('using MRAID polyfill');
    const polyfillOptions: any = {
      viewableChangeStrategy: VIEWABLE_CHANGE_STRATEGY.BIND_ON_BROWSER_EVENTS
    };
    if (!options.isMraidCompliant && isInternal(options.dsp || '')) {
      polyfillOptions.viewableChangeStrategy =
        VIEWABLE_CHANGE_STRATEGY.ALWAYS_VISIBLE;
    }
    window.mraid = getMraidPolyfill(polyfillOptions);
  }

  injectMRAIDScript(onSuccess: () => void, onError: (err?: any) => void): void {
    const head = document.getElementsByTagName('head').item(0);
    const js = document.createElement('script');
    js.onerror = err => {
      onError(err);
    };
    js.onload = () => {
      if (window.mraid) onSuccess();
      else onError();
    };
    js.setAttribute('type', 'text/javascript');
    js.setAttribute('src', 'mraid.js');
    head?.appendChild(js);
  }
}
